import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  BADGE_POSITIONS,
  CARD_LIMITS,
  CARD_RANGES,
  CardBadge,
  CardChip,
  CardLayoutTweaks,
  CardOverride,
  CardPositions,
  CardStoreDetail,
  CardStyle,
  CardTagline,
  ChipPresets,
  NAME_POSITIONS,
  PHOTO_ACCEPT,
  TAGLINE_FONTS,
  TIME_POSITIONS,
  TaglineFont,
  emptyOverride,
  getCardStore,
  isHttpUrl,
  mergeStyle,
  removeCardStore,
  saveCardStore,
  uploadCardPhoto,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/restaurantCardsService";
import { ColorField, SliderRow, pct, times } from "../ThemeStudio/fields";
import { GREEN, GREEN_DARK, errMsg } from "../ThemeStudio/themeUtils";
import { CardPreview, CardPreviewData, SAMPLE_CARD } from "./CardPreview";
import { CARD_ICON_NAMES, CardIcon, cardIconLabel } from "./cardIcons";
import {
  LAYOUT_LABELS,
  LayoutPicker,
  POSITION_LABELS,
  Section,
  btnGreen,
  btnOutline,
  switchSx,
} from "./CardLookTab";

type Draft = Omit<CardOverride, "badge" | "tagline"> & { badge: CardBadge; tagline: CardTagline };

const DEFAULT_BADGE: CardBadge = { mode: "auto", text: "", color: "#E53935", icon: "local-fire-department" };
const DEFAULT_TAGLINE: CardTagline = { text: "", font: "script", color: "#FFFFFF", x: 0.78, y: 0.45, size: 1, rotate: -8 };

const FONT_LABELS: Record<TaglineFont, string> = { script: "Script", bold: "Bold", serif: "Serif" };

const toDraft = (o: CardOverride | null): Draft => {
  const base = o ?? emptyOverride();
  return {
    ...base,
    gallery: [...base.gallery],
    chips: base.chips.map((c) => ({ ...c })),
    layout: { ...base.layout, ...(base.layout.positions ? { positions: { ...base.layout.positions } } : {}) },
    badge: { ...DEFAULT_BADGE, ...(base.badge ?? {}) },
    tagline: { ...DEFAULT_TAGLINE, ...(base.tagline ?? {}) },
  };
};

/** What PUT receives: an empty tagline means "no tagline". */
const toPayload = (d: Draft): CardOverride => ({
  enabled: d.enabled,
  cover_image_url: d.cover_image_url && isHttpUrl(d.cover_image_url) ? d.cover_image_url.trim() : null,
  gallery: d.gallery.filter((u) => isHttpUrl(u)).slice(0, CARD_LIMITS.gallery),
  badge: { ...d.badge, text: d.badge.text.trim() },
  tagline: d.tagline.text.trim() ? { ...d.tagline, text: d.tagline.text.trim() } : null,
  chips: d.chips.slice(0, CARD_LIMITS.chips),
  layout: d.layout,
  pin_rank: d.pin_rank,
});

const sameDraft = (a: Draft, b: Draft) => JSON.stringify(toPayload(a)) === JSON.stringify(toPayload(b));

const move = <T,>(list: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
};

/** Icon dropdown with a preview of each icon. */
const IconSelect: React.FC<{ label: string; value: string; onChange: (v: string) => void; sx?: object }> = ({
  label,
  value,
  onChange,
  sx,
}) => (
  <TextField
    select
    size="small"
    label={label}
    value={CARD_ICON_NAMES.includes(value) ? value : ""}
    onChange={(e) => onChange(e.target.value)}
    sx={{ minWidth: 170, ...sx }}
    SelectProps={{
      renderValue: (v) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <CardIcon name={v as string} sx={{ fontSize: 18, color: "text.secondary" }} />
          <span>{cardIconLabel(v as string)}</span>
        </Stack>
      ),
    }}
  >
    {CARD_ICON_NAMES.map((n) => (
      <MenuItem key={n} value={n}>
        <CardIcon name={n} sx={{ fontSize: 18, mr: 1, color: "text.secondary" }} />
        {cardIconLabel(n)}
      </MenuItem>
    ))}
  </TextField>
);

const Thumb: React.FC<{ src: string; onRemove: () => void; children?: React.ReactNode; label: string }> = ({
  src,
  onRemove,
  children,
  label,
}) => (
  <Box sx={{ position: "relative", width: 112 }}>
    <Box
      component="img"
      src={src}
      alt={label}
      sx={{ width: 112, height: 72, objectFit: "cover", borderRadius: 2, border: "1px solid rgba(0,0,0,0.12)", display: "block", bgcolor: "#eee" }}
    />
    <IconButton
      size="small"
      onClick={onRemove}
      aria-label={`Remove ${label}`}
      sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(255,255,255,0.92)", "&:hover": { bgcolor: "#fff" }, p: 0.25 }}
    >
      <CloseIcon sx={{ fontSize: 16 }} />
    </IconButton>
    {children}
  </Box>
);

/** Hidden file input + button; checks type and size before uploading. */
const UploadButton: React.FC<{
  storeId: number;
  label: string;
  disabled?: boolean;
  onUploaded: (url: string) => void;
  onError: (msg: string) => void;
}> = ({ storeId, label, disabled, onUploaded, onError }) => {
  const ref = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      onError("Use a PNG, JPG or WebP photo.");
      return;
    }
    if (file.size > CARD_LIMITS.photoMb * 1024 * 1024) {
      onError(`Photos can be up to ${CARD_LIMITS.photoMb} MB.`);
      return;
    }
    setBusy(true);
    try {
      onUploaded(await uploadCardPhoto(storeId, file));
    } catch (e) {
      onError(errMsg(e, "Upload failed. Try again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={PHOTO_ACCEPT}
        hidden
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <Button
        variant="outlined"
        startIcon={busy ? <CircularProgress size={16} sx={{ color: GREEN }} /> : <CloudUploadOutlinedIcon />}
        disabled={disabled || busy}
        onClick={() => ref.current?.click()}
        sx={{ ...btnOutline, height: 40, whiteSpace: "nowrap", flexShrink: 0 }}
      >
        {busy ? "Uploading…" : label}
      </Button>
    </>
  );
};

/** URL box with an Add button (for pasting a photo link). */
const PasteUrl: React.FC<{ label: string; disabled?: boolean; onAdd: (url: string) => void }> = ({ label, disabled, onAdd }) => {
  const [text, setText] = React.useState("");
  const bad = !!text.trim() && !isHttpUrl(text);
  const add = () => {
    if (!text.trim() || bad) return;
    onAdd(text.trim());
    setText("");
  };
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1, minWidth: 220 }}>
      <TextField
        size="small"
        label={label}
        placeholder="https://…"
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
        error={bad}
        helperText={bad ? "Must be an http(s) link" : undefined}
        fullWidth
      />
      <Button variant="text" onClick={add} disabled={disabled || !text.trim() || bad} sx={{ textTransform: "none", color: GREEN_DARK, height: 40 }}>
        Use link
      </Button>
    </Stack>
  );
};

/** "Use global (…)" select for one position tweak. */
const TweakSelect: React.FC<{
  label: string;
  options: readonly string[];
  value: string | undefined;
  globalValue: string;
  onChange: (v: string | undefined) => void;
}> = ({ label, options, value, globalValue, onChange }) => (
  <TextField
    select
    size="small"
    fullWidth
    label={label}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value || undefined)}
    SelectProps={{ displayEmpty: true }}
    InputLabelProps={{ shrink: true }}
  >
    <MenuItem value="">Use global ({POSITION_LABELS[globalValue] ?? globalValue})</MenuItem>
    {options.map((p) => (
      <MenuItem key={p} value={p}>
        {POSITION_LABELS[p] ?? p}
      </MenuItem>
    ))}
  </TextField>
);

const autoChips = (foodType: string | null | undefined, presets: ChipPresets): CardChip[] => {
  const out: CardChip[] = [];
  const veg = presets["pure-veg"];
  const quick = presets["quick-delivery"];
  if (foodType === "pure-veg" && veg) out.push({ key: "pure-veg", ...veg });
  if (quick) out.push({ key: "quick-delivery", ...quick });
  return out;
};

export const RestaurantCardEditor: React.FC<{
  storeId: number;
  storeName: string;
  globalStyle: CardStyle;
  chipPresets: ChipPresets;
  onClose: () => void;
  /** After a save or a removal, so the list can refresh. */
  onChanged: () => void;
  onToast: (msg: string, ok: boolean) => void;
}> = ({ storeId, storeName, globalStyle, chipPresets, onClose, onChanged, onToast }) => {
  const [detail, setDetail] = React.useState<CardStoreDetail | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [initial, setInitial] = React.useState<Draft>(toDraft(null));
  const [draft, setDraft] = React.useState<Draft>(toDraft(null));
  const [saving, setSaving] = React.useState(false);
  const [confirm, setConfirm] = React.useState<"discard" | "remove" | null>(null);
  const [removing, setRemoving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [customChip, setCustomChip] = React.useState({ label: "", icon: "thumb-up" });

  const load = React.useCallback(async () => {
    setLoadError(null);
    setDetail(null);
    try {
      const d = await getCardStore(storeId);
      setDetail(d);
      const dr = toDraft(d.override);
      setInitial(dr);
      setDraft(dr);
    } catch (e) {
      setLoadError(errMsg(e, "Could not load this restaurant's card."));
    }
  }, [storeId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const dirty = !!detail && !sameDraft(draft, initial);
  const tryClose = () => (dirty ? setConfirm("discard") : onClose());

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setBadge = (p: Partial<CardBadge>) => setDraft((d) => ({ ...d, badge: { ...d.badge, ...p } }));
  const setTagline = (p: Partial<CardTagline>) => setDraft((d) => ({ ...d, tagline: { ...d.tagline, ...p } }));
  const setTweak = (p: Partial<Omit<CardLayoutTweaks, "positions">>) =>
    setDraft((d) => {
      const layout: CardLayoutTweaks = { ...d.layout, ...p };
      (Object.keys(p) as Array<keyof typeof p>).forEach((k) => {
        if (p[k] === undefined) delete layout[k];
      });
      return { ...d, layout };
    });
  const setPosTweak = <K extends keyof CardPositions>(k: K, v: CardPositions[K] | undefined) =>
    setDraft((d) => {
      const positions: Partial<CardPositions> = { ...(d.layout.positions ?? {}) };
      if (v === undefined) delete positions[k];
      else positions[k] = v;
      const layout: CardLayoutTweaks = { ...d.layout, positions };
      if (!Object.keys(positions).length) delete layout.positions;
      return { ...d, layout };
    });

  const style = mergeStyle(globalStyle, draft.layout);
  const foodType = detail?.store.food_type ?? null;

  const previewBadge = (): CardPreviewData["badge"] => {
    const b = draft.badge;
    if (b.mode === "none") return null;
    if (b.mode === "custom" && b.text.trim()) return { text: b.text.trim(), color: b.color, icon: b.icon };
    if (foodType === "pure-veg") return { text: "Pure Veg", color: "#2E7D32", icon: "spa" };
    return { text: "Popular", color: "#E53935", icon: "local-fire-department" };
  };

  const photos = Array.from(
    new Set([draft.cover_image_url, ...draft.gallery].filter((u): u is string => !!u && isHttpUrl(u))),
  );
  const previewData: CardPreviewData = {
    ...SAMPLE_CARD,
    name: detail?.store.name || storeName,
    photos,
    badge: previewBadge(),
    tagline: draft.tagline.text.trim() ? draft.tagline : null,
    chips: draft.chips.length ? draft.chips : autoChips(foodType, chipPresets),
  };

  const badgeError = draft.badge.mode === "custom" && !draft.badge.text.trim() ? "Custom badges need some text." : null;
  const pinError =
    draft.pin_rank !== null &&
    (!Number.isInteger(draft.pin_rank) || draft.pin_rank < CARD_RANGES.pinRank.min || draft.pin_rank > CARD_RANGES.pinRank.max)
      ? "Rank must be a whole number from 1 to 100."
      : null;

  const save = async () => {
    if (badgeError || pinError) {
      setError(badgeError || pinError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await saveCardStore(storeId, toPayload(draft));
      setDetail(res);
      const dr = toDraft(res.override);
      setInitial(dr);
      setDraft(dr);
      onChanged();
      onToast("Saved. Apps update in a few seconds.", true);
    } catch (e) {
      setError(errMsg(e, "Could not save this card. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await removeCardStore(storeId);
      onChanged();
      onToast("Customisation removed. This restaurant now uses the default card.", true);
      setConfirm(null);
      onClose();
    } catch (e) {
      setConfirm(null);
      setError(errMsg(e, "Could not remove the customisation. Try again."));
    } finally {
      setRemoving(false);
    }
  };

  const chipKeys = new Set(draft.chips.map((c) => c.key));
  const chipsFull = draft.chips.length >= CARD_LIMITS.chips;
  const galleryFull = draft.gallery.length >= CARD_LIMITS.gallery;
  const uploadError = (msg: string) => setError(msg);

  return (
    <>
      <Dialog open onClose={tryClose} fullWidth maxWidth="lg" scroll="paper">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 6 }}>
          <Avatar src={detail?.store.logo_url ?? undefined} alt="" sx={{ width: 36, height: 36, bgcolor: "#E8F7EF", color: GREEN_DARK }}>
            {(detail?.store.name || storeName).charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }} noWrap>
              {detail?.store.name || storeName}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
              {detail?.override ? "Customised card" : "Default card. Save to customise it."}
              {dirty ? " · Unsaved changes" : ""}
            </Typography>
          </Box>
          <IconButton onClick={tryClose} aria-label="Close" sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#FAFAFB" }}>
          {loadError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={load} sx={{ textTransform: "none" }}>
                  Retry
                </Button>
              }
            >
              {loadError}
            </Alert>
          ) : !detail ? (
            <Box sx={{ py: 10, textAlign: "center" }}>
              <CircularProgress sx={{ color: GREEN }} />
            </Box>
          ) : (
            <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
              <Box sx={{ position: { md: "sticky" }, top: { md: 0 }, flexShrink: 0 }}>
                <Box sx={{ bgcolor: "#F0F1F4", borderRadius: 4, p: 2, width: 392, maxWidth: "100%", boxSizing: "border-box" }}>
                  <CardPreview
                    style={style}
                    data={previewData}
                    onTaglineMove={draft.tagline.text.trim() ? (x, y) => setTagline({ x, y }) : undefined}
                  />
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5, lineHeight: 1.5 }}>
                    {draft.tagline.text.trim() ? "Drag the tagline on the card to move it. " : ""}
                    Rating, distance and time are samples; the app shows this restaurant's own.
                    {draft.badge.mode === "auto" ? " The auto badge shown is an example." : ""}
                    {!draft.chips.length ? " With no chips picked, the app shows chips its data supports." : ""}
                  </Typography>
                  {!draft.enabled && (
                    <Alert severity="info" sx={{ mt: 1.5, fontSize: 12.5 }}>
                      Customisation is off, so the app shows the default card.
                    </Alert>
                  )}
                </Box>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                {error && (
                  <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Section title="Customisation">
                  <FormControlLabel
                    control={<Switch checked={draft.enabled} onChange={(e) => set("enabled", e.target.checked)} sx={switchSx} />}
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 13.5 }}>Use this customisation</Typography>
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                          Turn off to keep these settings but show the default card.
                        </Typography>
                      </Box>
                    }
                  />
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.pin_rank !== null}
                          onChange={(e) => set("pin_rank", e.target.checked ? 1 : null)}
                          sx={switchSx}
                        />
                      }
                      label={<Typography sx={{ fontSize: 13.5 }}>Pin to top</Typography>}
                    />
                    {draft.pin_rank !== null && (
                      <TextField
                        size="small"
                        type="number"
                        label="Rank"
                        value={Number.isFinite(draft.pin_rank) ? draft.pin_rank : ""}
                        onChange={(e) => set("pin_rank", e.target.value === "" ? NaN : Number(e.target.value))}
                        inputProps={{ min: CARD_RANGES.pinRank.min, max: CARD_RANGES.pinRank.max, step: 1 }}
                        error={!!pinError}
                        helperText={pinError ?? "1 shows first"}
                        sx={{ width: 140 }}
                      />
                    )}
                  </Stack>
                </Section>

                <Section title="Photos" hint={`PNG, JPG or WebP up to ${CARD_LIMITS.photoMb} MB. The cover shows first; rolling photos follow it.`}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Cover photo</Typography>
                    {draft.cover_image_url ? (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Thumb src={draft.cover_image_url} label="cover photo" onRemove={() => set("cover_image_url", null)} />
                        <UploadButton storeId={storeId} label="Replace" onUploaded={(u) => set("cover_image_url", u)} onError={uploadError} />
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                        <UploadButton storeId={storeId} label="Upload cover" onUploaded={(u) => set("cover_image_url", u)} onError={uploadError} />
                        <PasteUrl label="Or paste a photo link" onAdd={(u) => set("cover_image_url", u)} />
                      </Stack>
                    )}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                      Rolling photos{" "}
                      <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", fontWeight: 400 }}>
                        ({draft.gallery.length}/{CARD_LIMITS.gallery})
                        {!globalStyle.photos.rolling ? " · rolling is off in the look for all cards" : ""}
                      </Typography>
                    </Typography>
                    {draft.gallery.length > 0 && (
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                        {draft.gallery.map((u, i) => (
                          <Thumb
                            key={`${u}-${i}`}
                            src={u}
                            label={`rolling photo ${i + 1}`}
                            onRemove={() => set("gallery", draft.gallery.filter((_, j) => j !== i))}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                              <IconButton size="small" disabled={i === 0} onClick={() => set("gallery", move(draft.gallery, i, i - 1))} aria-label="Move earlier">
                                <ChevronLeftIcon fontSize="small" />
                              </IconButton>
                              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{i + 1}</Typography>
                              <IconButton
                                size="small"
                                disabled={i === draft.gallery.length - 1}
                                onClick={() => set("gallery", move(draft.gallery, i, i + 1))}
                                aria-label="Move later"
                              >
                                <ChevronRightIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Thumb>
                        ))}
                      </Stack>
                    )}
                    <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                      <UploadButton
                        storeId={storeId}
                        label="Upload photo"
                        disabled={galleryFull}
                        onUploaded={(u) => setDraft((d) => ({ ...d, gallery: [...d.gallery, u].slice(0, CARD_LIMITS.gallery) }))}
                        onError={uploadError}
                      />
                      <PasteUrl
                        label="Or paste a photo link"
                        disabled={galleryFull}
                        onAdd={(u) => setDraft((d) => ({ ...d, gallery: [...d.gallery, u].slice(0, CARD_LIMITS.gallery) }))}
                      />
                    </Stack>
                  </Box>
                </Section>

                <Section title="Badge" hint="Auto picks an offer first, then Popular, Trending or Pure Veg from the restaurant's orders and menu.">
                  <RadioGroup row value={draft.badge.mode} onChange={(e) => setBadge({ mode: e.target.value as CardBadge["mode"] })}>
                    <FormControlLabel value="auto" control={<Radio sx={radioSx} />} label="Auto" />
                    <FormControlLabel value="custom" control={<Radio sx={radioSx} />} label="Custom" />
                    <FormControlLabel value="none" control={<Radio sx={radioSx} />} label="None" />
                  </RadioGroup>
                  {draft.badge.mode === "custom" && (
                    <>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                        <TextField
                          size="small"
                          label="Badge text"
                          value={draft.badge.text}
                          onChange={(e) => setBadge({ text: e.target.value.slice(0, CARD_LIMITS.badgeText) })}
                          placeholder="Flat 20% OFF"
                          error={!!badgeError}
                          helperText={badgeError ?? `${draft.badge.text.length}/${CARD_LIMITS.badgeText}`}
                          sx={{ flex: 1 }}
                        />
                        <IconSelect label="Icon" value={draft.badge.icon} onChange={(v) => setBadge({ icon: v })} />
                      </Stack>
                      <ColorField label="Badge colour" value={draft.badge.color} onChange={(v) => setBadge({ color: v })} />
                    </>
                  )}
                </Section>

                <Section title="Tagline" hint="A short line written on the photo. Leave it empty for no tagline.">
                  <TextField
                    size="small"
                    label="Tagline"
                    value={draft.tagline.text}
                    onChange={(e) => setTagline({ text: e.target.value.slice(0, CARD_LIMITS.taglineText) })}
                    placeholder="Authentic Biryani Experience"
                    helperText={`${draft.tagline.text.length}/${CARD_LIMITS.taglineText}`}
                    fullWidth
                  />
                  {draft.tagline.text.trim() && (
                    <>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                        <TextField
                          select
                          size="small"
                          label="Font"
                          value={draft.tagline.font}
                          onChange={(e) => setTagline({ font: e.target.value as TaglineFont })}
                          sx={{ minWidth: 150 }}
                        >
                          {TAGLINE_FONTS.map((f) => (
                            <MenuItem key={f} value={f}>
                              {FONT_LABELS[f]}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Box sx={{ flex: 1, width: "100%" }}>
                          <ColorField label="Tagline colour" value={draft.tagline.color} onChange={(v) => setTagline({ color: v })} />
                        </Box>
                      </Stack>
                      <SliderRow label="Size" {...CARD_RANGES.taglineSize} value={draft.tagline.size} onChange={(v) => setTagline({ size: v })} format={times} />
                      <SliderRow
                        label="Rotate"
                        {...CARD_RANGES.taglineRotate}
                        value={draft.tagline.rotate}
                        onChange={(v) => setTagline({ rotate: v })}
                        format={(v) => `${v}°`}
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                          Position: {pct(draft.tagline.x)} across, {pct(draft.tagline.y)} down
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setTagline({ x: DEFAULT_TAGLINE.x, y: DEFAULT_TAGLINE.y })}
                          sx={{ textTransform: "none", color: GREEN_DARK }}
                        >
                          Reset position
                        </Button>
                      </Stack>
                    </>
                  )}
                </Section>

                <Section
                  title="Feature chips"
                  hint={`Up to ${CARD_LIMITS.chips}. With none picked, the app shows chips its data supports (Pure Veg, Quick Delivery).`}
                >
                  {draft.chips.length > 0 && (
                    <Stack spacing={1}>
                      {draft.chips.map((c, i) => (
                        <Stack
                          key={`${c.key}-${i}`}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: 2, px: 1.5, py: 0.5, bgcolor: "#fff" }}
                        >
                          <CardIcon name={c.icon} sx={{ fontSize: 18, color: "text.secondary" }} />
                          <Typography sx={{ flex: 1, fontSize: 13.5 }}>{c.label}</Typography>
                          {c.key === "custom" && <Chip size="small" label="Custom" sx={{ height: 20, fontSize: 11 }} />}
                          <IconButton size="small" disabled={i === 0} onClick={() => set("chips", move(draft.chips, i, i - 1))} aria-label="Move up">
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={i === draft.chips.length - 1}
                            onClick={() => set("chips", move(draft.chips, i, i + 1))}
                            aria-label="Move down"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => set("chips", draft.chips.filter((_, j) => j !== i))} aria-label={`Remove ${c.label}`}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Add a preset</Typography>
                    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75 }}>
                      {Object.entries(chipPresets).map(([key, p]) => (
                        <Chip
                          key={key}
                          icon={<CardIcon name={p.icon} sx={{ fontSize: "16px !important" }} />}
                          label={p.label}
                          variant="outlined"
                          clickable
                          disabled={chipsFull || chipKeys.has(key)}
                          onClick={() => set("chips", [...draft.chips, { key, label: p.label, icon: p.icon }])}
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Or add your own</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
                      <TextField
                        size="small"
                        label="Chip text"
                        value={customChip.label}
                        disabled={chipsFull}
                        onChange={(e) => setCustomChip((c) => ({ ...c, label: e.target.value.slice(0, CARD_LIMITS.chipLabel) }))}
                        helperText={`${customChip.label.length}/${CARD_LIMITS.chipLabel}`}
                        sx={{ flex: 1 }}
                      />
                      <IconSelect label="Icon" value={customChip.icon} onChange={(v) => setCustomChip((c) => ({ ...c, icon: v }))} />
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        disabled={chipsFull || !customChip.label.trim()}
                        onClick={() => {
                          set("chips", [
                            ...draft.chips,
                            { key: "custom", label: customChip.label.trim(), icon: CARD_ICON_NAMES.includes(customChip.icon) ? customChip.icon : "label" },
                          ]);
                          setCustomChip({ label: "", icon: "thumb-up" });
                        }}
                        sx={{ ...btnOutline, height: 40 }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Box>
                </Section>

                <Section title="Layout for this restaurant" hint="Optional. Anything left on “Use global” follows the look for all cards.">
                  <LayoutPicker
                    value={draft.layout.layout}
                    onChange={(v) => setTweak({ layout: v })}
                    onUseGlobal={() => setTweak({ layout: undefined })}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Photo fade"
                      value={draft.layout.overlay ?? ""}
                      onChange={(e) => setTweak({ overlay: (e.target.value || undefined) as "dark" | "light" | undefined })}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Use global ({globalStyle.overlay === "light" ? "Light fade" : "Dark fade"})</MenuItem>
                      <MenuItem value="dark">Dark fade</MenuItem>
                      <MenuItem value="light">Light fade</MenuItem>
                    </TextField>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Feature chips"
                      value={draft.layout.positions?.chips === undefined ? "" : draft.layout.positions.chips ? "on" : "off"}
                      onChange={(e) => setPosTweak("chips", e.target.value === "" ? undefined : e.target.value === "on")}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Use global ({globalStyle.positions.chips ? "Shown" : "Hidden"})</MenuItem>
                      <MenuItem value="on">Shown</MenuItem>
                      <MenuItem value="off">Hidden</MenuItem>
                    </TextField>
                  </Stack>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.layout.overlayStrength !== undefined}
                          onChange={(e) => setTweak({ overlayStrength: e.target.checked ? globalStyle.overlayStrength : undefined })}
                          sx={switchSx}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 13.5 }}>
                          Own fade strength{" "}
                          {draft.layout.overlayStrength === undefined && (
                            <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                              (global: {pct(globalStyle.overlayStrength)})
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                    {draft.layout.overlayStrength !== undefined && (
                      <SliderRow
                        label="Fade strength"
                        {...CARD_RANGES.overlayStrength}
                        value={draft.layout.overlayStrength}
                        onChange={(v) => setTweak({ overlayStrength: v })}
                        format={pct}
                      />
                    )}
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TweakSelect
                      label="Name"
                      options={NAME_POSITIONS}
                      value={draft.layout.positions?.name}
                      globalValue={globalStyle.positions.name}
                      onChange={(v) => setPosTweak("name", v as CardPositions["name"] | undefined)}
                    />
                    <TweakSelect
                      label="Badge"
                      options={BADGE_POSITIONS}
                      value={draft.layout.positions?.badge}
                      globalValue={globalStyle.positions.badge}
                      onChange={(v) => setPosTweak("badge", v as CardPositions["badge"] | undefined)}
                    />
                    <TweakSelect
                      label="Delivery time"
                      options={TIME_POSITIONS}
                      value={draft.layout.positions?.time}
                      globalValue={globalStyle.positions.time}
                      onChange={(v) => setPosTweak("time", v as CardPositions["time"] | undefined)}
                    />
                  </Stack>
                  {draft.layout.layout && (
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      This restaurant uses the {LAYOUT_LABELS[draft.layout.layout].toLowerCase()} layout.
                    </Typography>
                  )}
                </Section>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {detail?.override && (
            <Button
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirm("remove")}
              disabled={saving || removing}
              sx={{ textTransform: "none", mr: "auto" }}
            >
              Remove customisation
            </Button>
          )}
          <Button onClick={tryClose} disabled={saving} sx={{ textTransform: "none", color: "text.secondary" }}>
            Cancel
          </Button>
          <Tooltip title={!dirty && detail?.override ? "No changes to save" : ""}>
            <span>
              <Button
                variant="contained"
                onClick={save}
                disabled={!detail || saving || (!dirty && !!detail?.override)}
                sx={btnGreen}
              >
                {saving ? "Saving…" : detail?.override ? "Save" : "Save customisation"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm === "discard"} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>You have unsaved changes to this card. They will be lost.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} sx={{ textTransform: "none", color: GREEN_DARK }}>
            Keep editing
          </Button>
          <Button
            color="error"
            onClick={() => {
              setConfirm(null);
              onClose();
            }}
            sx={{ textTransform: "none" }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm === "remove"} onClose={() => !removing && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove customisation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {detail?.store.name || storeName} goes back to the default card: its photos, badge, tagline, chips, layout and pin are cleared.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={removing} sx={{ textTransform: "none", color: GREEN_DARK }}>
            Keep it
          </Button>
          <Button color="error" variant="contained" onClick={remove} disabled={removing} sx={{ textTransform: "none", borderRadius: 2 }}>
            {removing ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const radioSx = { "&.Mui-checked": { color: GREEN } } as const;

export default RestaurantCardEditor;
